import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ProjectDetail, Approval, Comment, Attachment } from '../services/api';
import { useAuth } from '../auth/useAuth';
import { usePortalSettings } from '../auth/PortalSettingsContext';

// ── Helpers ───────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatCurrency(n: number | null) {
  if (!n) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0 });
}

function formatBytes(bytes: number) {
  if (bytes < 1024)       return `${bytes} B`;
  if (bytes < 1024*1024)  return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1024/1024).toFixed(1)} MB`;
}

function ApprovalStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    'Approved':  { label: 'Approved',  cls: 'badge badge-success' },
    'Denied':    { label: 'Denied',    cls: 'badge badge-danger'  },
    'Pending':   { label: 'Pending',   cls: 'badge badge-warning' },
    'Skipped':   { label: 'Skipped',   cls: 'badge badge-neutral' },
    'Revision':  { label: 'Revision',  cls: 'badge badge-warning' },
    'Paused':    { label: 'Paused',    cls: 'badge badge-neutral' },
  };
  const b = map[status] || { label: status, cls: 'badge badge-neutral' };
  return <span className={b.cls}>{b.label}</span>;
}

function ProjectStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    'Draft':           { label: 'Draft',        cls: 'badge badge-neutral' },
    'Director Review': { label: 'Dir Review',   cls: 'badge badge-warning' },
    'IT Review':       { label: 'IT Review',    cls: 'badge badge-warning' },
    'EMT Review':      { label: 'EMT Review',   cls: 'badge badge-warning' },
    'AGM Review':      { label: 'EMT Review',   cls: 'badge badge-warning' }, // legacy alias
    'CEO Review':      { label: 'CEO Review',   cls: 'badge badge-warning' },
    'Revision':        { label: 'Revision',     cls: 'badge badge-warning' },
    'Paused':          { label: 'Paused',       cls: 'badge badge-neutral' },
    'Approved':        { label: 'Approved',     cls: 'badge badge-success' },
    'IT Denied':       { label: 'IT Denied',    cls: 'badge badge-danger'  },
    'Director Denied': { label: 'Dir Denied',   cls: 'badge badge-danger'  },
    'EMT Denied':      { label: 'EMT Denied',   cls: 'badge badge-danger'  },
    'AGM Denied':      { label: 'EMT Denied',   cls: 'badge badge-danger'  }, // legacy alias
    'Denied':          { label: 'Denied',       cls: 'badge badge-danger'  },
  };
  const b = map[status] || { label: status, cls: 'badge badge-neutral' };
  return <span className={b.cls}>{b.label}</span>;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 16, padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text2)', width: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text1)', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}

function Section({ title, actions, children }: { title: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 600, fontSize: 14, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
        <span>{title}</span>
        {actions}
      </div>
      {children}
    </div>
  );
}

// ── Approval Step ─────────────────────────────────────────────
function ApprovalStep({ approval, stepNumber }: { approval: Approval; stepNumber: number }) {
  const isPending  = approval.Status === 'Pending';
  const isApproved = approval.Status === 'Approved';
  const isDenied   = approval.Status === 'Denied';
  const isSkipped  = approval.Status === 'Skipped';
  const isRevision = approval.Status === 'Revision';

  const circleColor = isApproved ? 'var(--success)'
    : isDenied || isRevision ? 'var(--danger)'
    : isPending ? 'var(--accent)'
    : 'var(--border2)';
  const circleText = isApproved ? '✓' : isDenied ? '✗' : isRevision ? '↩' : isSkipped ? '—' : stepNumber.toString();

  const displayName = approval.StepName === 'AGM' ? 'EMT' : approval.StepName;

  return (
    <div style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        background: circleColor, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 600,
      }}>
        {circleText}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{displayName}</span>
            <span style={{ color: 'var(--text2)', fontSize: 12, marginLeft: 8 }}>
              {approval.ApproverName || approval.ApproverRole}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {approval.ActionTakenAt && (
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{formatDateTime(approval.ActionTakenAt)}</span>
            )}
            <ApprovalStatusBadge status={approval.Status} />
          </div>
        </div>
        {approval.ApproverEmail && (
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: approval.Comments ? 8 : 0 }}>
            {approval.ApproverEmail}
          </div>
        )}
        {approval.Comments && (
          <div style={{
            fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)',
            borderRadius: 6, padding: '8px 12px', borderLeft: '2px solid var(--border2)', marginTop: 6,
          }}>
            <strong style={{ color: 'var(--text1)' }}>Note: </strong>{approval.Comments}
          </div>
        )}
        {isPending && (
          <div style={{
            fontSize: 12, color: 'var(--warning-text)', background: 'var(--warning-bg)',
            borderRadius: 6, padding: '6px 12px', borderLeft: '2px solid var(--warning)', marginTop: 6,
          }}>
            Awaiting action from {approval.ApproverName || approval.ApproverEmail || approval.ApproverRole}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Comment Thread ────────────────────────────────────────────
function CommentThread({ comments, isInternal, canManage, onMarkAnswered }: {
  comments: Comment[]; isInternal: boolean; canManage: boolean; onMarkAnswered: (commentId: number) => void;
}) {
  const visible = isInternal ? comments : comments.filter(c => !c.IsInternal);
  if (visible.length === 0) {
    return <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No comments yet.</div>;
  }
  return (
    <div>
      {visible.map((c, i) => {
        const initials = c.AuthorName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        return (
          <div key={c.CommentID} style={{
            display: 'flex', gap: 12, padding: '12px 0',
            borderBottom: i < visible.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: c.IsInternal ? 'var(--warning, #f59e0b)' : 'var(--accent)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 600, flexShrink: 0,
            }}>{initials}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {c.AuthorName}
                  {c.IsInternal && (
                    <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--warning-bg, #fffbeb)', color: 'var(--warning-text, #92400e)', border: '1px solid var(--warning, #f59e0b)', borderRadius: 4, padding: '1px 5px', fontWeight: 500 }}>
                      Internal
                    </span>
                  )}
                  {c.IsQuestion && (
                    <span className={`badge badge-${c.QuestionStatus === 'Answered' ? 'success' : 'warning'}`} style={{ marginLeft: 6, fontSize: 10 }}>
                      {c.QuestionStatus === 'Answered' ? 'Question — Answered' : 'Question — Open'}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{formatDateTime(c.CreatedAt)}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.5 }}>{c.CommentText}</div>
              {c.IsQuestion && c.QuestionStatus === 'Open' && canManage && (
                <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => onMarkAnswered(c.CommentID)}>Mark Answered</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CommentInput({ projectId, dbUserID, userName, userEmail, isAdmin, canFlagQuestion, onPosted }: {
  projectId:        number;
  dbUserID:         number | null;
  userName:         string;
  userEmail:        string;
  isAdmin:          boolean;
  canFlagQuestion:  boolean;
  onPosted:         () => void;
}) {
  const [text, setText]           = useState('');
  const [isInternal, setInternal] = useState(false);
  const [isQuestion, setQuestion] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);

  async function handleSubmit() {
    if (!text.trim()) return;
    if (!dbUserID)    { setError('You must be logged in to comment.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.comments.create(projectId, {
        commentText:  text.trim(),
        authorUserID: dbUserID,
        isInternal:   isAdmin ? isInternal : false,
        isQuestion:   canFlagQuestion ? isQuestion : false,
        actorEmail:   userEmail,
        actorName:    userName,
      });
      setText('');
      setInternal(false);
      setQuestion(false);
      onPosted();
    } catch (e: any) {
      setError(e.message || 'Failed to post comment.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <textarea
        className="field-input"
        style={{ width: '100%', minHeight: 80, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
        placeholder="Add a comment..."
        value={text}
        onChange={e => setText(e.target.value)}
        disabled={saving}
      />
      {error && (
        <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 4 }}>{error}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isInternal}
                onChange={e => setInternal(e.target.checked)}
                disabled={saving}
              />
              Internal note (IT only)
            </label>
          )}
          {canFlagQuestion && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isQuestion}
                onChange={e => setQuestion(e.target.checked)}
                disabled={saving}
              />
              Flag as question
            </label>
          )}
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSubmit}
          disabled={saving || !text.trim()}
        >
          {saving ? 'Posting...' : 'Post Comment'}
        </button>
      </div>
    </div>
  );
}

// ── Attachments Panel ─────────────────────────────────────────
function AttachmentsPanel({ projectId, canManage, attachments, onRefresh, dbUserID, userName, userEmail }: {
  projectId:   number;
  canManage:   boolean;
  attachments: Attachment[];
  onRefresh:   () => void;
  dbUserID:    number | null;
  userName:    string;
  userEmail:   string;
}) {
  const [deleting, setDeleting]             = useState<number | null>(null);
  const [downloading, setDownloading]       = useState<number | null>(null);
  const [uploadingFile, setUploadingFile]   = useState(false);
  const [uploadError, setUploadError]       = useState<string | null>(null);

  async function handleDelete(a: Attachment) {
    if (!window.confirm(`Delete "${a.FileName}"?`)) return;
    setDeleting(a.AttachmentID);
    try {
      await api.attachments.delete(projectId, a.AttachmentID);
      onRefresh();
    } catch (e: any) {
      alert('Failed to delete: ' + e.message);
    } finally {
      setDeleting(null);
    }
  }

  async function handleDownload(a: Attachment) {
    setDownloading(a.AttachmentID);
    try {
      const { url } = await api.attachments.getDownloadUrl(projectId, a.AttachmentID);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.FileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e: any) {
      alert('Failed to generate download link: ' + e.message);
    } finally {
      setDownloading(null);
    }
  }

  const iconForType = (mime: string | null) => {
    if (!mime) return '📎';
    if (mime.includes('pdf'))   return '📄';
    if (mime.includes('image')) return '🖼️';
    if (mime.includes('word') || mime.includes('docx')) return '📝';
    if (mime.includes('sheet') || mime.includes('xlsx')) return '📊';
    return '📎';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {attachments.map(a => (
        <div key={a.AttachmentID} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', borderRadius: 8,
          background: 'var(--surface2)', border: '1px solid var(--border)',
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>{iconForType(a.MimeType)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.FileName}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {formatBytes(a.FileSizeBytes)} · {a.UploadedByName} · {formatDate(a.UploadedAt)}
            </div>
          </div>
          <button
            className="btn btn-sm"
            onClick={() => handleDownload(a)}
            disabled={downloading === a.AttachmentID}
            style={{ flexShrink: 0 }}
          >
            {downloading === a.AttachmentID ? '...' : 'Download'}
          </button>
          {canManage && (
            <button
              className="btn btn-sm"
              onClick={() => handleDelete(a)}
              disabled={deleting === a.AttachmentID}
              style={{ color: 'var(--danger-text, #991b1b)', flexShrink: 0 }}
            >
              {deleting === a.AttachmentID ? '...' : 'Delete'}
            </button>
          )}
        </div>
      ))}

      {attachments.length === 0 && (
        <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
          No attachments uploaded.
        </div>
      )}

      {canManage && (
        <div style={{ marginTop: 8 }}>
          {uploadError && (
            <div style={{ fontSize: 12, color: 'var(--danger-text)', marginBottom: 8, whiteSpace: 'pre-line' }}>
              {uploadError}
            </div>
          )}
          <label style={{ cursor: uploadingFile ? 'default' : 'pointer' }}>
            <input
              type="file" multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              style={{ display: 'none' }}
              disabled={uploadingFile}
              onChange={async e => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setUploadingFile(true);
                setUploadError(null);
                const errors: string[] = [];
                try {
                  for (const file of files) {
                    if (file.size > 25 * 1024 * 1024) {
                      errors.push(`${file.name} exceeds the 25MB limit and was skipped.`);
                      continue;
                    }
                    try {
                      const { blobUrl, blobName } = await api.attachments.getSasToken(projectId, file.name);
                      const uploadRes = await fetch(blobUrl, {
                        method: 'PUT',
                        headers: {
                          'x-ms-blob-type': 'BlockBlob',
                          'Content-Type': file.type || 'application/octet-stream',
                        },
                        body: file,
                      });
                      if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`);
                      const permanentUrl = blobUrl.split('?')[0];
                      await api.attachments.create(projectId, {
                        fileName:         file.name,
                        blobUrl:          permanentUrl,
                        blobName,
                        fileSizeBytes:    file.size,
                        mimeType:         file.type || null,
                        uploadedByUserID: dbUserID,
                        actorEmail:       userEmail,
                        actorName:        userName,
                      });
                    } catch (fileErr: any) {
                      errors.push(`${file.name}: ${fileErr.message}`);
                    }
                  }
                  if (errors.length > 0) setUploadError(errors.join('\n'));
                  onRefresh();
                } finally {
                  setUploadingFile(false);
                  e.target.value = '';
                }
              }}
            />
            <span className="btn btn-sm" style={{ pointerEvents: 'none' }}>
              {uploadingFile ? 'Uploading...' : '+ Upload File'}
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

// ── Requirements Tab ──────────────────────────────────────────
interface ScopeItem {
  ScopeItemID:    number;
  ProjectID:      number;
  Category:       string | null;
  Question:       string;
  VendorResponse: string | null;
  Status:         'Open' | 'Awaiting' | 'Answered' | 'Resolved' | 'NA';
  IsRequired:     boolean;
  AddedByName:    string | null;
  ResolvedAt:     string | null;
  CreatedAt:      string;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    'Resolved': 'badge-success', 'Answered': 'badge-info',
    'Awaiting': 'badge-warning', 'Open': 'badge-neutral', 'NA': 'badge-neutral',
  };
  return <span className={`badge ${map[status] || 'badge-neutral'}`}>{status}</span>;
}

const STATUSES   = ['Open', 'Awaiting', 'Answered', 'Resolved', 'NA'];
const CATEGORIES = ['Functional', 'Technical', 'Security', 'Integration', 'Accessibility', 'Reporting', 'Training', 'Other'];

function ScopeItemRow({ item, canEdit, onUpdated }: { item: ScopeItem; canEdit: boolean; onUpdated: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [response, setResponse] = useState(item.VendorResponse || '');
  const [status, setStatus]     = useState(item.Status);
  const [saving, setSaving]     = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await api.scope.update(item.ScopeItemID, { vendorResponse: response || null, status });
      onUpdated(); setExpanded(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ borderBottom: '1px solid var(--border)', background: item.Status === 'Resolved' ? 'var(--surface2, #f8fafc)' : 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px' }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', marginTop: 6, flexShrink: 0, background: item.IsRequired ? 'var(--danger, #ef4444)' : 'var(--warning, #f59e0b)' }} title={item.IsRequired ? 'Required' : 'Nice-to-have'} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', marginBottom: 4, lineHeight: 1.5 }}>{item.Question}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {item.Category && <span>📁 {item.Category}</span>}
            {item.AddedByName && <span>👤 {item.AddedByName}</span>}
            {item.IsRequired ? <span style={{ color: 'var(--danger, #ef4444)' }}>● Required</span> : <span style={{ color: 'var(--warning, #f59e0b)' }}>● Nice-to-have</span>}
          </div>
          {item.VendorResponse && (
            <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)', borderRadius: 6, padding: '7px 12px', borderLeft: '2px solid var(--border2)', marginTop: 8 }}>
              <strong style={{ color: 'var(--text1)' }}>Response: </strong>{item.VendorResponse}
            </div>
          )}
          {expanded && canEdit && (
            <div style={{ marginTop: 12, padding: 14, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Status</label>
                <select className="field-select" value={status} onChange={e => setStatus(e.target.value as any)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Vendor Response / Answer</label>
                <textarea className="field-input" style={{ width: '100%', minHeight: 70, fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
                  placeholder="Enter the vendor's response or your team's answer..."
                  value={response} onChange={e => setResponse(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button className="btn btn-sm" onClick={() => setExpanded(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
          <StatusBadge status={item.Status} />
          {canEdit && (
            <button className="btn btn-sm" onClick={() => setExpanded(e => !e)} style={{ fontSize: 11 }}>
              {expanded ? 'Close' : 'Edit / Respond'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddScopeItemForm({ projectId, userName, onSave, onCancel }: {
  projectId: number; userName: string; onSave: () => void; onCancel: () => void;
}) {
  const [form, setForm] = useState({ category: '', question: '', isRequired: true });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  async function handleSave() {
    if (!form.question.trim()) { setError('Please enter a requirement or question.'); return; }
    setSaving(true);
    try {
      await api.scope.create(projectId, { category: form.category || null, question: form.question.trim(), isRequired: form.isRequired ? 1 : 0, addedByName: userName });
      onSave();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ background: 'var(--surface2, #f8fafc)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Add Requirement or Question</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Category</label>
          <select className="field-select" style={{ width: '100%' }} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
            <option value="">— Select category —</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Priority</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {[true, false].map(val => (
              <label key={String(val)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" checked={form.isRequired === val} onChange={() => setForm(f => ({ ...f, isRequired: val }))} />
                {val ? '🔴 Required' : '🟡 Nice-to-have'}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Requirement or Question</label>
        <textarea className="field-input" style={{ width: '100%', minHeight: 80, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
          placeholder="e.g. Must integrate with Tyler EPL for permit data."
          value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} />
      </div>
      {error && <div style={{ color: 'var(--danger-text, #991b1b)', fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Requirement'}</button>
        <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function RequirementsTab({ projectId, canEdit, userName }: { projectId: number; canEdit: boolean; userName: string }) {
  const [items, setItems]     = useState<ScopeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('All');
  const [showAdd, setShowAdd] = useState(false);

  const loadItems = useCallback(() => {
    setLoading(true);
    api.scope.list(projectId).then(data => setItems(data as unknown as ScopeItem[])).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const FILTERS  = ['All', 'Open', 'Awaiting', 'Answered', 'Resolved'];
  const filtered = items.filter(item => filter === 'All' || item.Status === filter);
  const grouped  = filtered.reduce<Record<string, ScopeItem[]>>((acc, item) => {
    const key = item.Category || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  const resolvedCount = items.filter(i => i.Status === 'Resolved').length;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Loading requirements...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              fontSize: 12, padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
              border: '1px solid var(--border2)',
              background: filter === f ? 'var(--accent)' : 'var(--surface)',
              color: filter === f ? '#fff' : 'var(--text2)',
              fontWeight: filter === f ? 600 : 400,
            }}>{f}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {items.length > 0 && (
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>
              <span style={{ color: 'var(--success-text, #065f46)', fontWeight: 600 }}>{resolvedCount}</span>{' / '}{items.length} resolved
            </span>
          )}
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(s => !s)}>+ Add Requirement</button>
          )}
        </div>
      </div>

      {showAdd && (
        <AddScopeItemForm
          projectId={projectId}
          userName={userName}
          onSave={() => { setShowAdd(false); loadItems(); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {items.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>No requirements yet</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Requirements track what this project needs to deliver and questions for vendors.</div>
          {canEdit && <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add First Requirement</button>}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>No items match the selected filter.</div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '11px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
            <span>{filtered.length} requirement{filtered.length !== 1 ? 's' : ''}</span>
            <div style={{ display: 'flex', gap: 16 }}>
              <span>🔴 {items.filter(i => i.IsRequired).length} required</span>
              <span>🟡 {items.filter(i => !i.IsRequired).length} nice-to-have</span>
              <span style={{ color: 'var(--success-text, #065f46)' }}>✅ {resolvedCount} resolved</span>
            </div>
          </div>
          {Object.entries(grouped).map(([category, categoryItems]) => (
            <div key={category}>
              <div style={{ padding: '8px 18px', background: 'var(--surface2, #f8fafc)', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>
                {category} ({categoryItems.length})
              </div>
              {categoryItems.map(item => (
                <ScopeItemRow key={item.ScopeItemID} item={item} canEdit={canEdit} onUpdated={loadItems} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────

type Tab = 'details' | 'requirements' | 'attachments';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin, dbUserID } = useAuth();
  const { requirementsTrackerVisible } = usePortalSettings();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('details');

  const loadProject = useCallback(() => {
    if (!id) return;
    api.projects.get(parseInt(id))
      .then(setProject)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { loadProject(); }, [loadProject]);

  async function handleMarkAnswered(commentId: number) {
    if (!id) return;
    try {
      await api.comments.markAnswered(parseInt(id), commentId);
      loadProject();
    } catch { /* non-fatal — user can retry */ }
  }

  const [checkingReplies, setCheckingReplies] = useState(false);
  async function handleCheckReplies() {
    setCheckingReplies(true);
    try {
      await api.comments.checkReplies();
      loadProject();
    } catch { /* non-fatal — the 5-minute timer will still pick it up */ }
    finally { setCheckingReplies(false); }
  }

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>Loading project...</div>;

  if (error || !project) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <div style={{ color: 'var(--danger-text)', marginBottom: 12 }}>{error || 'Project not found.'}</div>
        <button className="btn" onClick={() => navigate('/projects')}>← Back to Projects</button>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'details',      label: 'Details & Approvals' },
    ...(requirementsTrackerVisible || isAdmin ? [{ id: 'requirements' as Tab, label: 'Requirements' }] : []),
    { id: 'attachments',  label: `Attachments${project.attachments.length > 0 ? ` (${project.attachments.length})` : ''}` },
  ];

  const isRevision = project.ApprovalStatus === 'Revision';
  const isPaused   = project.ApprovalStatus === 'Paused';
  const isOwner    = project.RequestorEmail === user?.email;

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <button className="btn" style={{ marginBottom: 10, fontSize: 12 }} onClick={() => navigate('/projects')}>
            ← All Projects
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>{project.Title}</h1>
            <ProjectStatusBadge status={project.ApprovalStatus} />
            {project.Priority && project.Priority !== 'TBD' && (
              <span className={`badge badge-${project.Priority === 'High' || project.Priority === 'Critical' ? 'danger' : project.Priority === 'Low' ? 'neutral' : 'info'}`}>
                {project.Priority}
              </span>
            )}
          </div>
          <p className="page-sub" style={{ marginTop: 4 }}>
            Submitted by {project.RequestorName} · {project.DepartmentName}
            {project.SubmittedAt && ` · ${formatDate(project.SubmittedAt)}`}
            {project.AssignedToName && ` · Assigned to ${project.AssignedToName}`}
          </p>
        </div>
      </div>

      {/* ── Revision banner ── */}
      {isRevision && isOwner && (
        <div style={{
          background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning, #f59e0b)',
          borderRadius: 10, padding: '14px 18px', marginBottom: 16,
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--warning-text, #92400e)', marginBottom: 6 }}>
            ✏️ Revision Requested
          </div>
          {project.RevisionComment && (
            <div style={{ fontSize: 13, color: 'var(--text1)', marginBottom: 10 }}>"{project.RevisionComment}"</div>
          )}
          <button className="btn btn-sm" onClick={() => navigate(`/new?revise=${project.ProjectID}`)}>
            Revise & Resubmit →
          </button>
        </div>
      )}

      {/* ── Paused banner ── */}
      {isPaused && (
        <div style={{
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '12px 18px', marginBottom: 16,
          fontSize: 13, color: 'var(--text2)',
        }}>
          ⏸ This project is currently paused by IT. Contact the IT team for more information.
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '9px 20px', border: 'none',
            borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -2, background: 'none',
            color: activeTab === tab.id ? 'var(--accent)' : 'var(--text2)',
            fontWeight: activeTab === tab.id ? 600 : 400,
            fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.12s',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Details & Approvals ── */}
      {activeTab === 'details' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>
          <div>
            <Section title="Project Information">
              <InfoRow label="Department"       value={project.DepartmentName} />
              <InfoRow label="Requestor"        value={project.RequestorName} />
              <InfoRow label="Business Owner"   value={project.BusinessOwnerName} />
              <InfoRow label="Project Manager"  value={project.ProjectManagerName} />
              <InfoRow label="Priority"         value={project.Priority} />
              <InfoRow label="Budgeted"         value={project.Budgeted} />
              <InfoRow label="Budget Amount"    value={formatCurrency(project.AmountBudgeted)} />
              <InfoRow label="Start Date"       value={formatDate(project.StartDate)} />
              <InfoRow label="Target End Date"  value={formatDate(project.EndDate)} />
              <InfoRow label="Project Status"   value={project.ProjectStatus} />
              {project.AssignedToName && <InfoRow label="Assigned To (IT)" value={project.AssignedToName} />}
            </Section>

            <Section title="Project Description">
              {project.ProjectDescription && (
                <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 16 }}>{project.ProjectDescription}</div>
              )}
              {project.Objectives && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Objectives</div>
                  <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 16 }}>{project.Objectives}</div>
                </>
              )}
              {project.Deliverables && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Deliverables</div>
                  <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 16 }}>{project.Deliverables}</div>
                </>
              )}
              {project.OrganizationalBenefits && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Organizational Benefits</div>
                  <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6 }}>{project.OrganizationalBenefits}</div>
                </>
              )}
            </Section>

            {(() => {
              const visibleComments = project.comments.filter(c => isAdmin || !c.IsInternal);
              const openQuestions   = visibleComments.filter(c => c.IsQuestion && c.QuestionStatus === 'Open').length;
              const canFlagQuestion = isAdmin || project.approvals.some(a =>
                a.Status === 'Pending' && a.ApproverEmail && user?.email &&
                a.ApproverEmail.toLowerCase() === user.email.toLowerCase()
              );
              return (
                <Section
                  title={`Activity & Comments (${visibleComments.length})`}
                  actions={
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleCheckReplies}
                      disabled={checkingReplies}
                      title="Check now for email replies to questions/comments on this project"
                    >
                      {checkingReplies ? 'Checking...' : '🔄 Check for replies'}
                    </button>
                  }
                >
                  {openQuestions > 0 && (
                    <span className="badge badge-warning" style={{ marginBottom: 10, display: 'inline-block' }}>
                      {openQuestions} open question{openQuestions === 1 ? '' : 's'}
                    </span>
                  )}
                  <CommentThread comments={project.comments} isInternal={isAdmin} canManage={isAdmin} onMarkAnswered={handleMarkAnswered} />
                  <CommentInput
                    projectId={project.ProjectID}
                    dbUserID={dbUserID}
                    userName={user?.name || ''}
                    userEmail={user?.email || ''}
                    isAdmin={isAdmin}
                    canFlagQuestion={canFlagQuestion}
                    onPosted={loadProject}
                  />
                </Section>
              );
            })()}
          </div>

          <div>
            <Section title="Approval Chain">
              {project.approvals.map((a, i) => (
                <ApprovalStep key={a.ApprovalID} approval={a} stepNumber={i + 1} />
              ))}
              {project.approvals.length === 0 && (
                <div style={{ padding: '12px 0', color: 'var(--text3)', fontSize: 13 }}>No approval steps found.</div>
              )}
            </Section>

            <Section title="Approvers">
              <InfoRow label="Director"     value={project.DirectorName} />
              <InfoRow label="Dir Email"    value={project.DirectorEmail} />
              <InfoRow label="EMT Contact"  value={(project as any).AGMName} />
              <InfoRow label="EMT Email"    value={(project as any).AGMEmail} />
            </Section>

            {project.ITNotes && (
              <Section title="IT Notes">
                <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6 }}>{project.ITNotes}</div>
              </Section>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Requirements ── */}
      {activeTab === 'requirements' && (
        <RequirementsTab
          projectId={project.ProjectID}
          canEdit={isAdmin}
          userName={user?.name || 'Unknown'}
        />
      )}

      {/* ── Tab: Attachments ── */}
      {activeTab === 'attachments' && (
        <Section title="Project Attachments">
          <AttachmentsPanel
            projectId={project.ProjectID}
            canManage={isAdmin || project.RequestorEmail === user?.email}
            attachments={project.attachments}
            onRefresh={loadProject}
            dbUserID={dbUserID}
            userName={user?.name || ''}
            userEmail={user?.email || ''}
          />
        </Section>
      )}
    </div>
  );
}
